import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Sparkles, ArrowRight, Clock, Heart, Package, TrendingUp } from 'lucide-react';
import { API } from './GlassCard';
import { GlassCard, GradientIconTile, CountUp } from '@/components/dashboard/dashboard-ui';
import { useAuth } from '@/context/AuthContext';

const POLL_MS = 20_000; // 20s — vendors love live money updates

/**
 * Gold-flashy vendor earnings hero — shows recent inbound money + active
 * orders with click-through. Polls every 20s and on tab focus.
 *
 * Layout (top → bottom):
 *  1. "Money in" pulse card — deposits + finals + tips just received
 *  2. Active orders list (clickable rows → /dashboard/vendor/orders/{id})
 */
export default function VendorEarningsPulse() {
  const { getAuthHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [oRes, sRes] = await Promise.all([
        fetch(`${API}/custom-orders/mine?role=vendor`, { headers: getAuthHeaders() }),
        fetch(`${API}/stats/vendor`, { headers: getAuthHeaders() }),
      ]);
      if (oRes.ok) setOrders((await oRes.json()).orders || []);
      if (sRes.ok) setStats(await sRes.json());
    } catch { /* poll again later */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    const onRefresh = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('vendor-stats-refresh', onRefresh);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('vendor-stats-refresh', onRefresh);
    };
  }, [load]);

  if (!stats && orders.length === 0) return null;

  const breakdown = stats?.earnings_breakdown || {};
  const justReceived = (breakdown.custom_deposit || 0) + (breakdown.custom_final || 0) + (breakdown.tips || 0);
  const pendingFinal = breakdown.custom_pending_final || 0;
  const tipsTotal = breakdown.tips || 0;

  const activeOrders = orders.filter((o) => ['active', 'revision_requested', 'delivered'].includes(o.status));
  const pendingPaymentOrders = orders.filter((o) => o.status === 'pending_payment');

  return (
    <div className="space-y-4" data-testid="vendor-earnings-pulse">
      {/* 🟡 GOLD FLASHY EARNINGS HERO */}
      {justReceived > 0 && (
        <div
          className="relative rounded-2xl overflow-hidden border-2 border-yellow-300/60 bg-gradient-to-r from-yellow-400/15 via-amber-400/10 to-yellow-300/15 p-5"
          style={{ animation: 'earnings-pulse 2s ease-in-out infinite' }}
          data-testid="earnings-hero"
        >
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-yellow-300/10 blur-2xl" />
          <div className="relative flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-yellow-400 flex items-center justify-center shadow-[0_0_30px_rgba(252,211,77,0.7)] flex-shrink-0">
              <DollarSign size={26} className="text-[#0E0E23]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-yellow-300" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-yellow-200/90 font-bold">Money in your account</p>
              </div>
              <p className="text-3xl sm:text-4xl font-bold text-yellow-50 font-syne mt-1" data-testid="earnings-amount">
                $<CountUp value={justReceived} decimals={2} duration={1400} />
              </p>
              <p className="text-yellow-100/80 text-xs mt-1">
                Released to your Stripe account from completed work + deposits + tips.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {(breakdown.custom_deposit || 0) > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-yellow-300/15 border border-yellow-300/30 text-yellow-100 text-[11px] font-bold">
                    50% deposits · ${breakdown.custom_deposit.toFixed(2)}
                  </span>
                )}
                {(breakdown.custom_final || 0) > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-emerald-100 text-[11px] font-bold">
                    Final payouts · ${breakdown.custom_final.toFixed(2)}
                  </span>
                )}
                {tipsTotal > 0 && (
                  <span className="px-2.5 py-1 rounded-full bg-rose-400/15 border border-rose-400/30 text-rose-100 text-[11px] font-bold inline-flex items-center gap-1">
                    <Heart size={10} className="fill-rose-200 text-rose-200" /> Tips · ${tipsTotal.toFixed(2)}
                  </span>
                )}
              </div>
              {pendingFinal > 0 && (
                <p className="mt-3 text-yellow-100 text-sm">
                  <TrendingUp size={12} className="inline mr-1" />
                  <span className="font-bold">${pendingFinal.toFixed(2)} more</span> on the way when you complete + buyer approves your active jobs. 🎉
                </p>
              )}
            </div>
            <Link
              to="/dashboard/vendor/earnings"
              className="px-4 py-2.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-[#0E0E23] text-xs font-bold inline-flex items-center gap-1.5 transition-all flex-shrink-0"
              data-testid="view-earnings-cta"
            >
              View earnings <ArrowRight size={12} />
            </Link>
          </div>
          <style>{`
            @keyframes earnings-pulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(252, 211, 77, 0.35); }
              50% { box-shadow: 0 0 32px 4px rgba(252, 211, 77, 0.6); }
            }
          `}</style>
        </div>
      )}

      {/* Pending payment heads-up */}
      {pendingPaymentOrders.length > 0 && (
        <GlassCard className="p-4 border-yellow-400/30 bg-yellow-500/[0.04]">
          <div className="flex items-center gap-3">
            <Clock size={18} className="text-yellow-300" />
            <p className="text-yellow-100 text-sm">
              <span className="font-bold">{pendingPaymentOrders.length} buyer{pendingPaymentOrders.length === 1 ? '' : 's'}</span> still need{pendingPaymentOrders.length === 1 ? 's' : ''} to pay your offer
              {' '}(<span className="font-bold">${pendingPaymentOrders.reduce((s, o) => s + (o.price || 0), 0).toFixed(2)}</span> incoming if they do).
            </p>
          </div>
        </GlassCard>
      )}

      {/* 🟢 Active Orders list */}
      {activeOrders.length > 0 && (
        <GlassCard glow className="p-5" data-testid="active-orders-list">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <GradientIconTile icon={Package} ramp="lime" size="sm" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold mb-0.5">Live</p>
                <h3 className="text-white font-bold text-base font-syne flex items-center gap-2">
                  Active orders
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-100 text-[10px] font-bold">
                    {activeOrders.length}
                  </span>
                </h3>
              </div>
            </div>
            <Link to="/dashboard/vendor/orders" className="text-xs text-zinc-400 hover:text-white">View all →</Link>
          </div>
          <div className="space-y-2">
            {activeOrders.slice(0, 5).map((o) => (
              <Link
                key={o.order_id}
                to={`/dashboard/vendor/orders/${o.order_id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04] border border-white/5 hover:border-emerald-400/20 transition-all group"
                data-testid={`active-order-${o.order_id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-400/20 flex items-center justify-center flex-shrink-0">
                  <Package size={14} className="text-emerald-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-sm truncate">{o.title || 'Custom project'}</p>
                  <p className="text-zinc-400 text-xs">
                    ${(o.price || 0).toFixed(2)} ·{' '}
                    {o.status === 'delivered' ? 'Awaiting buyer approval'
                      : o.status === 'revision_requested' ? 'Buyer requested revision'
                      : `${o.delivery_days || '?'}-day delivery`}
                  </p>
                </div>
                <ArrowRight size={14} className="text-zinc-500 group-hover:text-white group-hover:translate-x-1 transition-all flex-shrink-0" />
              </Link>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
